import { definePreview, EmbeddedComponentMeta, Inputs } from '@embeddable.com/react';
import { Value, loadData, LoadDataRequest, DataResponse } from '@embeddable.com/core';
import Component, { MAX_OPTIONS } from '.';
import { inputs } from '../../component.inputs.constants';
import { previewData } from '../../preview.data.constants';

const meta = {
  name: 'MultiSelectFieldPro',
  label: 'Multi Select Field',
  description:
    'Multi-select dropdown of dimension values from a dataset. Pairs with a string-array variable used in dataset filters; emits onChange.',
  category: 'Dropdowns - values',
  defaultWidth: 300,
  defaultHeight: 120,
  inputs: [
    inputs.dataset,
    { ...inputs.dimension, label: 'Dimension (to load Dropdown values)' },
    inputs.title,
    inputs.description,
    inputs.tooltip,
    { ...inputs.placeholder, defaultValue: 'Select values...' },
    {
      ...inputs.number,
      name: 'maxOptions',
      label: 'Maximum options',
      category: 'Component Settings',
      defaultValue: MAX_OPTIONS,
    },
    {
      ...inputs.string,
      name: 'selectedValues',
      array: true,
      label: 'Selected values',
      category: 'Pre-configured Variables',
    },
    {
      ...inputs.dimension,
      required: false,
      name: 'optionalSecondDimension',
      label: 'Optional secondary dimension',
      category: 'Data Mapping for Interactions',
      description: 'Send a different dimension to embeddable when the user clicks. Must be unique.',
    },
  ],
  events: [
    {
      name: 'onChange',
      label: 'Selected values updated',
      properties: [
        {
          name: 'value',
          label: 'Selected values',
          type: 'string',
          array: true,
        },
      ],
    },
  ],
  variables: [
    {
      name: 'multi-select values',
      type: 'string',
      array: true,
      defaultValue: Value.noFilter(),
      inputs: ['selectedValues'],
      events: [{ name: 'onChange', property: 'value' }],
    },
  ],
} as const satisfies EmbeddedComponentMeta;

export type MultiSelectDropdownState = {
  searchValue?: string;
};

const preview = definePreview(Component, {
  dimension: previewData.dimension,
  results: previewData.results1Measure1Dimension,
  onChange: () => null,
});

const loadDataResultsArgs = (
  inputs: Inputs<typeof meta>,
  state?: MultiSelectDropdownState,
): LoadDataRequest => {
  const operator = inputs.dimension.nativeType === 'string' ? 'contains' : 'equals';
  return {
    limit: inputs.maxOptions,
    from: inputs.dataset,
    select: [inputs.dimension, inputs.optionalSecondDimension].filter(Boolean),
    filters: state?.searchValue
      ? [{ operator, property: inputs.dimension, value: state.searchValue }]
      : undefined,
  };
};

const loadDataResults = (
  inputs: Inputs<typeof meta>,
  state?: MultiSelectDropdownState,
): DataResponse => loadData(loadDataResultsArgs(inputs, state));

const props = (
  inputs: Inputs<typeof meta>,
  [state, setState]: [MultiSelectDropdownState, (state: MultiSelectDropdownState) => void],
) => ({
  ...inputs,
  setSearchValue: (searchValue: string) => setState({ searchValue }),
  results: loadDataResults(inputs, state),
});

const events = {
  onChange: (selectedValues: string[]) => {
    return {
      value: selectedValues.length ? selectedValues : Value.noFilter(),
    };
  },
};

export const multiSelectFieldPro = {
  Component,
  meta,
  preview,
  config: {
    props,
    events,
  },
  results: {
    loadDataArgs: loadDataResultsArgs,
    loadData: loadDataResults,
  },
} as const;
